'use client'

import { API_ORIGIN, authorizedFetch, useEvaluationSummary } from '@yeshuman/sdk'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { Opportunity } from './use-opportunities'
import type { CandidateOpportunityMatchLocationState } from './use-navigate-to-candidate-opportunity-match'

export function useCandidateOpportunityMatchState() {
  const { evaluationId = '' } = useParams<{ evaluationId: string }>()
  const location = useLocation()
  const matchState = (location.state as CandidateOpportunityMatchLocationState | null) ?? {}

  const { evaluation, isLoading: evaluationLoading, error: evaluationError } =
    useEvaluationSummary(evaluationId)

  const opportunityId =
    evaluation?.opportunity_id ?? matchState.opportunityId ?? undefined

  const opportunityQuery = useQuery({
    queryKey: ['opportunity', 'match-detail', opportunityId],
    enabled: Boolean(opportunityId),
    queryFn: async (): Promise<Opportunity> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/${encodeURIComponent(opportunityId!)}`,
      )
      if (!response.ok) {
        throw new Error(`Failed to load job (${response.status})`)
      }
      return response.json()
    },
    staleTime: 60_000,
  })

  const displayTitle = useMemo(() => {
    if (matchState.label?.trim()) return matchState.label.trim()
    if (opportunityQuery.data?.title) return opportunityQuery.data.title
    return 'Job match'
  }, [matchState.label, opportunityQuery.data?.title])

  const companyName = opportunityQuery.data?.organisation_name

  const isLoading = evaluationLoading || (Boolean(opportunityId) && opportunityQuery.isLoading)
  const missingRequired = !evaluationId
  const missingData =
    evaluationError != null ||
    !evaluation ||
    (Boolean(opportunityId) && (opportunityQuery.error != null || !opportunityQuery.data))

  return {
    evaluationId,
    opportunityId,
    opportunity: opportunityQuery.data,
    displayTitle,
    companyName,
    isLoading,
    missingRequired,
    missingData,
  }
}
